import { useEffect, useState, useMemo, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmt, MACHINE_GROUPS, DAYS } from "@/lib/ui";
import { toast } from "sonner";
import { X, CalendarRange, Loader2 } from "lucide-react";

const TYPES = ["Dizgi", "Bonnel", "Tablet"];
const SHIFTS = [
  { key: "gunduz", label: "Gündüz" },
  { key: "gece", label: "Gece" },
];

export default function Schedule() {
  const { isEditor } = useAuth();
  const [orders, setOrders] = useState([]);
  const [horizon, setHorizon] = useState([]);
  const [type, setType] = useState("Dizgi");
  const [edit, setEdit] = useState(null); // order being scheduled
  const [daily, setDaily] = useState({});
  const [loading, setLoading] = useState(true);
  const [dragId, setDragId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/production-orders"),
      api.get("/projection?weeks=12"),
    ]).then(([o, p]) => {
      setOrders(o.data);
      setHorizon(p.data.horizon);
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail))).finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const machines = MACHINE_GROUPS[type];

  // weeks = horizon labels ∪ order weeks (bu tipteki), sıralı
  const weeks = useMemo(() => {
    const set = new Set(horizon.map((h) => h.label));
    orders.forEach((o) => {
      if (o.machine && o.machine.startsWith(type) && o.week && o.week !== "AKTİF OLMAYAN") set.add(o.week);
    });
    return [...set].sort();
  }, [horizon, orders, type]);

  const cellOrders = (machine, week) =>
    orders
      .filter((o) => o.machine === machine && o.week === week)
      .sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));

  const onDropCell = async (machine, week, beforeId) => {
    if (!dragId) return;
    const dragged = orders.find((o) => o.id === dragId);
    setDragId(null);
    if (!dragged) return;
    if (dragged.machine === machine && dragged.week === week && (beforeId === dragged.id || beforeId === null)) {
      // aynı hücre, aynı yer -> değişiklik yok
      if (beforeId === null) return;
    }
    let list = cellOrders(machine, week).filter((o) => o.id !== dragId).map((o) => o.id);
    let at = beforeId ? list.indexOf(beforeId) : list.length;
    if (at < 0) at = list.length;
    list.splice(at, 0, dragId);
    try {
      await Promise.all(list.map((id, i) => {
        const body = { seq: i };
        if (id === dragId) { body.machine = machine; body.week = week; }
        return api.put(`/production-orders/${id}`, body);
      }));
      toast.success("Sipariş taşındı");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openEdit = (o) => {
    setEdit(o);
    const d = {};
    for (let i = 0; i < 7; i++) {
      d[i] = { gunduz: o.daily?.[i]?.gunduz ?? "", gece: o.daily?.[i]?.gece ?? "" };
    }
    setDaily(d);
  };

  const dailyTotal = useMemo(() => {
    let t = 0;
    Object.values(daily).forEach((d) => {
      t += (parseFloat(d.gunduz) || 0) + (parseFloat(d.gece) || 0);
    });
    return t;
  }, [daily]);

  const saveDaily = async () => {
    const clean = {};
    Object.entries(daily).forEach(([k, v]) => {
      const g = parseFloat(v.gunduz) || 0;
      const n = parseFloat(v.gece) || 0;
      if (g || n) clean[k] = { gunduz: g, gece: n };
    });
    try {
      await api.put(`/production-orders/${edit.id}`, { daily: clean });
      toast.success("Günlük program kaydedildi");
      setEdit(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 animate-in-up" data-testid="schedule-page">
      <div>
        <h1 className="font-head text-3xl font-bold tracking-tight">Üretim Çizelgesi</h1>
        <p className="text-[#94A3B8] text-sm mt-1">
          Hafta × makine planı. Siparişleri sürükleyerek başka hafta/makineye taşıyın veya sıralayın. Bir siparişe tıklayıp günlük (gündüz/gece) üretim adetlerini girin.
        </p>
      </div>

      <div className="flex gap-1.5 flex-wrap" data-testid="schedule-type-tabs">
        {TYPES.map((t) => (
          <button key={t} data-testid={`schedule-type-${t}`} onClick={() => setType(t)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              type === t ? "bg-[#2563EB] text-white" : "bg-[#131A26] border border-[#212D40] text-[#94A3B8] hover:text-white"
            }`}>
            {t} ({MACHINE_GROUPS[t].length})
          </button>
        ))}
      </div>

      <div className="bg-[#131A26] border border-[#212D40] rounded-xl overflow-hidden">
        <div className="overflow-auto max-h-[calc(100vh-280px)]">
          <table className="text-sm border-collapse" data-testid="schedule-table">
            <thead className="sticky top-0 z-20">
              <tr className="bg-[#0F1520]">
                <th className="py-3 px-4 text-left text-xs uppercase tracking-wider text-[#94A3B8] sticky left-0 bg-[#0F1520] z-30 border-b border-r border-[#212D40] min-w-[110px]">
                  Hafta
                </th>
                {machines.map((mc) => (
                  <th key={mc} className="py-3 px-3 text-left text-xs uppercase tracking-wider text-[#94A3B8] border-b border-r border-[#212D40] min-w-[170px]">
                    {mc}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!loading && weeks.map((wk) => (
                <tr key={wk} className="border-b border-[#1a2334]">
                  <td className="py-2 px-4 font-num font-semibold sticky left-0 bg-[#131A26] z-10 border-r border-[#212D40] whitespace-nowrap">
                    <span className="flex items-center gap-1.5"><CalendarRange className="w-3.5 h-3.5 text-[#2563EB]" />{wk}</span>
                  </td>
                  {machines.map((mc) => {
                    const items = cellOrders(mc, wk);
                    return (
                      <td key={mc} className="py-2 px-2 align-top border-r border-[#1a2334]" data-testid={`schedule-cell-${mc}-${wk}`}
                        onDragOver={(e) => { if (isEditor && dragId) e.preventDefault(); }}
                        onDrop={(e) => { if (isEditor) { e.preventDefault(); onDropCell(mc, wk, null); } }}>
                        <div className="space-y-1.5">
                          {items.map((o) => {
                            const hasDaily = o.daily && Object.keys(o.daily).length > 0;
                            return (
                              <button key={o.id} onClick={() => isEditor && openEdit(o)}
                                data-testid={`schedule-order-${o.id}`}
                                disabled={!isEditor}
                                draggable={isEditor}
                                onDragStart={(e) => { setDragId(o.id); e.dataTransfer.effectAllowed = "move"; }}
                                onDragOver={(e) => { if (isEditor && dragId) { e.preventDefault(); e.stopPropagation(); } }}
                                onDrop={(e) => { if (isEditor && dragId) { e.preventDefault(); e.stopPropagation(); onDropCell(mc, wk, o.id); } }}
                                className={`w-full text-left rounded-md px-2 py-1.5 text-xs border transition-colors ${
                                  dragId === o.id ? "opacity-40 " : ""
                                }${
                                  hasDaily
                                    ? "bg-[rgba(16,185,129,0.12)] border-[rgba(16,185,129,0.4)] hover:bg-[rgba(16,185,129,0.2)]"
                                    : "bg-[#0F1520] border-[#212D40] hover:border-[#2563EB]"
                                }`}>
                                <div className="font-semibold truncate">{o.order_name || o.order_code || o.order_id}</div>
                                <div className="text-[#94A3B8] flex justify-between gap-2">
                                  <span className="truncate">{o.wire_raw || "—"}</span>
                                  <span className="font-num shrink-0">{fmt(o.quantity)}</span>
                                </div>
                              </button>
                            );
                          })}
                          {items.length === 0 && <span className="text-[10px] text-[#3a4658]">—</span>}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
              {loading && (
                <tr><td colSpan={machines.length + 1} className="py-10 text-center text-[#64748B]">
                  <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Yükleniyor...
                </td></tr>
              )}
              {!loading && weeks.length === 0 && (
                <tr><td colSpan={machines.length + 1} className="py-10 text-center text-[#64748B]">Hafta bulunamadı.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-xs text-[#64748B]">
        Yeşil kutular günlük programı girilmiş siparişleri gösterir. Makine ataması Üretim Planı sayfasında sipariş girerken yapılır.
      </p>

      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-[#131A26] border border-[#212D40] rounded-xl w-full max-w-2xl max-h-[88vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-[#212D40]">
              <div>
                <h3 className="font-head text-xl font-semibold">{edit.order_name || edit.order_code || edit.order_id}</h3>
                <p className="text-xs text-[#94A3B8] mt-0.5">
                  {edit.machine} · {edit.week} · Tel: {edit.wire_raw || "—"} · Toplam: <span className="font-num">{fmt(edit.quantity)}</span>
                </p>
              </div>
              <button onClick={() => setEdit(null)}><X className="w-5 h-5 text-[#94A3B8]" /></button>
            </div>
            <div className="p-5 overflow-y-auto">
              <table className="w-full text-sm" data-testid="schedule-daily-grid">
                <thead>
                  <tr className="text-xs text-[#94A3B8] uppercase tracking-wider">
                    <th className="py-2 text-left">Vardiya</th>
                    {DAYS.map((d) => <th key={d} className="py-2 text-center">{d}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {SHIFTS.map((sh) => (
                    <tr key={sh.key}>
                      <td className="py-2 pr-2 font-medium whitespace-nowrap">{sh.label}</td>
                      {DAYS.map((_, i) => (
                        <td key={i} className="py-1 px-1">
                          <input type="number" data-testid={`schedule-input-${sh.key}-${i}`}
                            value={daily[i]?.[sh.key] ?? ""}
                            onChange={(e) => setDaily({ ...daily, [i]: { ...daily[i], [sh.key]: e.target.value } })}
                            className="w-full bg-[#0F1520] border border-[#212D40] rounded px-1.5 py-1.5 text-center font-num text-xs outline-none focus:border-[#2563EB]" />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-3 text-sm text-[#94A3B8]">
                Girilen toplam: <span className="font-num text-white">{fmt(dailyTotal)}</span> / {fmt(edit.quantity)}
                {dailyTotal > edit.quantity && <span className="text-[#F59E0B] ml-2">(sipariş adedini aşıyor)</span>}
              </div>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-[#212D40]">
              <button onClick={() => setEdit(null)} className="px-4 py-2 rounded-lg text-sm text-[#94A3B8] hover:bg-[#0F1520]">İptal</button>
              <button data-testid="schedule-save" onClick={saveDaily} className="px-5 py-2 rounded-lg text-sm font-semibold bg-[#2563EB] hover:bg-[#1D4ED8]">Kaydet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
